import { Component, OnInit, inject } from '@angular/core';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { DropdownModule } from 'primeng/dropdown';
import { LocationProducts, NetsapiensSite, Product } from '@voice/utils';
import { FormsModule } from '@angular/forms';
import { ButtonDirective } from 'primeng/button';
import { CurrencyPipe, NgIf } from '@angular/common';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import {
  StaticServicesService,
  StaticServiceCode,
} from '../../../services/static-services.service';
import { BillingService } from '../../../services/billing.service';
import { firstValueFrom } from 'rxjs';
import {
  formatCurrency,
  parseRateInput,
  isValidStaticServiceForm,
  StaticServiceFormData,
} from './utils/product-form.utils';

interface StaticServiceFormDialogData {
  record?: { location?: string };
  sites?: NetsapiensSite[];
}

@Component({
  selector: 'app-client-onboarding-static-service-form',
  standalone: true,
  imports: [
    DropdownModule,
    FormsModule,
    ButtonDirective,
    CurrencyPipe,
    NgIf,
    InputTextModule,
    CalendarModule,
  ],
  templateUrl: './static-service-form.component.html',
})
export class StaticServiceFormComponent implements OnInit {
  private readonly dialogConfig = inject<DynamicDialogConfig<StaticServiceFormDialogData>>(
    DynamicDialogConfig
  );
  private readonly dialogRef = inject(DynamicDialogRef);
  private readonly staticServicesService = inject(StaticServicesService);
  private readonly billingService = inject(BillingService);

  location?: string;
  sites: NetsapiensSite[] = [];
  availableStaticServiceCodes: StaticServiceCode[] = [];
  selectedStaticServiceCode?: StaticServiceCode;
  quantity = 1;
  rate?: number;
  catalogRate?: number;
  description?: string;
  effectiveDate?: Date;
  products: Product[] = [];

  readonly formatCurrency = formatCurrency;

  async ngOnInit(): Promise<void> {
    this.initializeFormData();
    await Promise.all([
      this.loadStaticServiceCodes(),
      this.loadProducts(),
    ]);
  }

  onStaticServiceCodeSelected(code: StaticServiceCode): void {
    if (code) {
      this.setupStaticServiceCode(code);
    } else {
      this.clearStaticServiceCode();
    }
  }

  onRateChange(value: unknown): void {
    this.rate = parseRateInput(value);
  }

  save(): void {
    if (!isValidStaticServiceForm(this.getFormData())) {
      return;
    }

    const product = this.buildProduct();
    const result: LocationProducts = {
      location: this.location!,
      products: [product],
    };
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  private initializeFormData(): void {
    const data = this.dialogConfig.data;
    this.location = data?.record?.location;
    this.sites = data?.sites || [];
  }

  private loadStaticServiceCodes(): void {
    this.staticServicesService.getStaticServiceCodes().subscribe({
      next: (codes) => {
        this.availableStaticServiceCodes = codes;
      },
      error: (err) => {
        console.error('Error loading static service codes:', err);
      },
    });
  }

  private async loadProducts(): Promise<void> {
    try {
      this.products = await firstValueFrom(this.billingService.getProducts());
    } catch (err) {
      console.error('Error loading products:', err);
    }
  }

  private setupStaticServiceCode(code: StaticServiceCode): void {
    this.selectedStaticServiceCode = code;
    this.description = code.description;
    this.updateRateFromProduct(code.code);
  }

  private updateRateFromProduct(code: string): void {
    const product = this.products.find((p) => p.code === code);
    if (product?.rate !== undefined && product.rate !== null) {
      this.catalogRate = product.rate;
      this.rate = product.rate;
    } else {
      this.catalogRate = undefined;
      this.rate = undefined;
    }
  }

  private clearStaticServiceCode(): void {
    this.selectedStaticServiceCode = undefined;
    this.description = undefined;
    this.catalogRate = undefined;
    this.rate = undefined;
  }

  private buildProduct(): Product {
    if (!this.selectedStaticServiceCode || !this.location) {
      throw new Error('Invalid form state: missing required fields');
    }

    return {
      code: this.selectedStaticServiceCode.code,
      description: this.description || this.selectedStaticServiceCode.description,
      rate: this.rate ?? this.catalogRate,
      quantity: this.quantity,
      effectiveDate: this.effectiveDate,
    };
  }

  private getFormData(): StaticServiceFormData {
    return {
      location: this.location,
      selectedStaticServiceCode: this.selectedStaticServiceCode,
      quantity: this.quantity,
      rate: this.rate,
      catalogRate: this.catalogRate,
      description: this.description,
      effectiveDate: this.effectiveDate,
    };
  }
}
